import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { usePagination } from "../hooks";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputBase,
  TablePagination,
  Skeleton,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import { SCREEN_IDS } from "../components/FullComponent.js";
import { translateText } from "../translations/translationService";

import { typeConnect, backgroundColor2 } from "../constants/Constants";
import { getFullMo, insertNewWeight } from "../services/ConnectDataBase";
import { FINAL_WEIGHING_ADMIN_COLORS } from "../style/colors.js";
import { getCurrentDateTime, formatDate } from "../services/Functions";
import { updateListaPesos } from "../redux/reducers";
import { notify } from "../services/ui/notify";
import AppDialog from "../components/Common/ui/AppDialog";
import AppButton from "../components/Common/ui/AppButton";

export default function FinalWeighingAdminSection() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const screenName = "FinalWeighingAdminSection";
  const currentScreenId = SCREEN_IDS.ADMIN_FINISH_WEIGHT;
  const tipo_tiempo = "WEIGHT";
  const lang = useSelector((state) => state.lang.value);
  const listData = useSelector((state) => state.pesosList.listaPesos);
  const [refSearch, setRefSearch] = useState("");
  const usuario = useSelector((state) => state.user.name);
  const equipo = useSelector((state) => state.user.team);
  const [searchResult, setSearchResult] = useState(null);
  const [weights, setWeights] = useState({});
  const [cantidadInput, setCantidadInput] = useState({});
  // Confirmación (reemplaza window.confirm)
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const confirmResolverRef = useRef(null);

  const askConfirm = (message) => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmMessage(message);
      setConfirmVisible(true);
    });
  };
  // Hook de paginación
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(0, 20);

  const getMo = useCallback(async () => {
    setIsLoading(true);
    try {
      const fullMo = await getFullMo(
        screenName,
        typeConnect[0],
        currentScreenId
      );
      const moArray = Object.entries(fullMo).map(([po, mo]) => ({
        key: po,
        po,
        ...mo,
      }));
      setIsLoading(false);
      dispatch(updateListaPesos(moArray));

      const initialWeights = moArray.reduce((acc, item) => {
        acc[item.key] = "";
        return acc;
      }, {});

      const initialCantidad = moArray.reduce((acc, item) => {
        acc[item.key] = "";
        return acc;
      }, {});
      setWeights(initialWeights);
      setCantidadInput(initialCantidad);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  }, [dispatch, screenName, currentScreenId]);

  useEffect(() => {
    getMo();
  }, [getMo]);

  const handleWeightChange = (mo, weight) => {
    if (/^\d+(\.\d*)?$/.test(weight) || weight === "") {
      setWeights((prevWeights) => ({
        ...prevWeights,
        [mo]: weight,
      }));
    }
  };

  const handleCantidadChange = (mo, cantidad) => {
    if (/^\d+(\.\d*)?$/.test(cantidad) || cantidad === "") {
      setCantidadInput((prevCantidadInput) => ({
        ...prevCantidadInput,
        [mo]: cantidad,
      }));
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearch = () => {
    const trimmedRefSearch = refSearch.trim();
    const result = listData.find(
      (item) => item.mo === trimmedRefSearch.replace("'", "-")
    );
    setSearchResult(result);
    setRefSearch("");
    handleChangePage(null, 0);
  };

  const handleAdd = async (item) => {
    let newCantidad = cantidadInput[item.key];
    let newWeight = weights[item.key];

    let limiteSuperior = newCantidad * (item.gramos * 1.07);
    let limiteInferior = newCantidad * (item.gramos * 0.97);
    let pendiente = item.cantidad - item.cantidad_terminada;

    if (/^\d+(\.\d+)?$/.test(newWeight)) {
      if (newCantidad <= pendiente) {
        let mensajeConfirmacion = `¿El peso: ${newWeight} es correcto?`;
        if (newWeight >= limiteInferior && newWeight <= limiteSuperior) {
          if (newWeight && parseFloat(newWeight) !== 0) {
            const status = await insertNewWeight(
              item.po,
              item.mo,
              usuario,
              equipo,
              1,
              newWeight,
              getCurrentDateTime(),
              "Term.",
              tipo_tiempo,
              currentScreenId,
              newCantidad
            );
            if (status) {
              const updatedListData = listData.filter(
                (rowData) => rowData.key !== item.key
              );
              dispatch(updateListaPesos(updatedListData));
              setSearchResult(null);
            }
          } else {
            console.log("Valor de peso inválido.");
          }
        } else {
          const test = await askConfirm(mensajeConfirmacion);
          if (test) {
            if (newWeight && parseFloat(newWeight) !== 0) {
              const status = await insertNewWeight(
                item.po,
                item.mo,
                usuario,
                equipo,
                1,
                newWeight,
                getCurrentDateTime(),
                "Term.",
                tipo_tiempo,
                currentScreenId,
                newCantidad
              );
              if (status) {
                const updatedListData = listData.filter(
                  (rowData) => rowData.key !== item.key
                );
                dispatch(updateListaPesos(updatedListData));
                setSearchResult(null);
              }
            } else {
              console.log("Valor de peso inválido.");
            }
          }
        }
      } else {
        notify.warn("La cantidad ingresada excede la pendiente.");
      }
    } else {
      notify.warn("El peso no es correcto.");
    }
  };


  const renderList = () => {
    const dataList = searchResult ? [searchResult] : listData;

    if (listData.length === 0) {
      return (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
            textAlign: "center",
            py: 8,
          }}>
          <Typography variant='h6' sx={{ color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_SECONDARY || "#6B7280" }}>
            {translateText("Todos los pesos añadidos", lang)}
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
        }}>
        <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
          <Table stickyHeader size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("PO", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("MO", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Cantidad", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Referencia", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Entrega", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Stamping", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Cant. Term.", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Peso", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Añadir Peso", lang)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item) => {
                  const moWeight = weights[item.key] || "";
                  const cantidadTerminada = cantidadInput[item.key] || "";
                  const calculatedPlaceholder = `aprox: ${Math.round(
                    item.gramos * (item.cantidad - item.cantidad_terminada)
                  )} gr`;
                  const cantidadPlaceholder = `Res: ${Math.round(
                    item.cantidad - item.cantidad_terminada
                  )}`;
                  return (
                    <TableRow
                      key={item.key}
                      sx={{
                        "&:hover": {
                          backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
                        },
                        borderBottom: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                      }}>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.po}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.mo}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.cantidad}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.referencia}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {formatDate(item.fechaentrega)}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.stamping}
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <TextField
                          value={cantidadTerminada}
                          onChange={(e) =>
                            handleCantidadChange(item.key, e.target.value)
                          }
                          type='number'
                          placeholder={cantidadPlaceholder}
                          size='small'
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              backgroundColor: "white",
                              height: "28px",
                              width: "120px",
                              "& fieldset": {
                                borderColor: FINAL_WEIGHING_ADMIN_COLORS.BORDER_LIGHT || "#e5e7eb",
                              },
                              "&:hover fieldset": {
                                borderColor: FINAL_WEIGHING_ADMIN_COLORS.BORDER_BRAND || "#174045",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: FINAL_WEIGHING_ADMIN_COLORS.BORDER_BRAND || "#174045",
                                borderWidth: "1px",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              textAlign: "center",
                              fontSize: "0.75rem",
                              py: 0.5,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <TextField
                          value={moWeight}
                          onChange={(e) =>
                            handleWeightChange(item.key, e.target.value)
                          }
                          type='number'
                          placeholder={calculatedPlaceholder}
                          size='small'
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              backgroundColor: "white",
                              height: "28px",
                              width: "120px",
                              "& fieldset": {
                                borderColor: FINAL_WEIGHING_ADMIN_COLORS.BORDER_LIGHT || "#e5e7eb",
                              },
                              "&:hover fieldset": {
                                borderColor: FINAL_WEIGHING_ADMIN_COLORS.BORDER_BRAND || "#174045",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: FINAL_WEIGHING_ADMIN_COLORS.BORDER_BRAND || "#174045",
                                borderWidth: "1px",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              textAlign: "center",
                              fontSize: "0.75rem",
                              py: 0.5,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <Button
                          onClick={() => handleAdd(item)}
                          variant='contained'
                          sx={{
                            width: "120px",
                            padding: "4px 12px",
                            fontSize: "0.75rem",
                            height: "28px",
                            backgroundColor: backgroundColor2,
                            textTransform: "none",
                            borderRadius: 1,
                            boxShadow: "none",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_HOVER || "#1e4d52",
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 4px rgba(70, 93, 100, 0.2)",
                            },
                          }}>
                          {translateText("Añadir", lang)}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          count={dataList.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage={translateText("Filas por página:", lang)}
          rowsPerPageOptions={[5, 10, 25, 50]}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
            "& .MuiTablePagination-toolbar": {
              fontSize: "0.75rem",
              color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_SECONDARY || "#6B7280",
              minHeight: "52px",
            },
            "& .MuiTablePagination-select": {
              fontSize: "0.75rem",
            },
            "& .MuiTablePagination-displayedRows": {
              fontSize: "0.75rem",
            },
          }}
        />
      </Paper>
    );
  };

  return (
    <Box sx={{ padding: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
        }}>
        <Box sx={{ flex: 1, minWidth: "280px", position: "relative" }}>
          <InputBase
            value={refSearch || ""}
            onChange={(e) => {
              let inputValue = e.target.value.replace(/'/g, "-");
              if (/^\d{6}-\d{3}$/.test(inputValue.split(" ").slice(-1)[0])) {
                inputValue += " ";
              }
              setRefSearch(inputValue);
            }}
            placeholder={translateText("Código de MO", lang)}
            onKeyPress={handleKeyPress}
            sx={{
              width: "100%",
              backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_CARD || "#F9FAFB",
              color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_PRIMARY || "#374151",
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: "8px 12px 8px 40px",
              borderRadius: 2,
              border: `1.5px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_CARD || "#e5e7eb"}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_INPUT || "#F3F4F6",
                borderColor: FINAL_WEIGHING_ADMIN_COLORS.BORDER_BRAND || "#174045",
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                borderColor: FINAL_WEIGHING_ADMIN_COLORS.BORDER_BRAND || "#174045",
                boxShadow:
                  "0 0 0 2px rgba(70, 93, 100, 0.1), 0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "& input": {
                padding: 0,
              },
              "& input::placeholder": {
                color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_MUTED || "#9CA3AF",
                opacity: 1,
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_MUTED || "#9CA3AF",
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        <Button
          onClick={handleSearch}
          variant='contained'
          size="small"
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_BUTTON_PRIMARY || "#174045",
            color: "white",
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BACKGROUND_BUTTON_SECONDARY || "#1e4d52",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Buscar", lang)}
        </Button>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: FINAL_WEIGHING_ADMIN_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <Typography variant='h6' sx={{ flex: 1, textAlign: "center", fontSize: "1rem", fontWeight: 600 }}>
          {translateText("Tabla de MO", lang)}
        </Typography>

        <IconButton
          onClick={getMo}
          sx={{
            padding: 1,
            color: FINAL_WEIGHING_ADMIN_COLORS.TEXT_BRAND || "#174045",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(70, 93, 100, 0.08)",
              transform: "scale(1.1)",
            },
          }}>
          <RestartAltIcon />
        </IconButton>
      </Box>
      {isLoading ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${FINAL_WEIGHING_ADMIN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      translateText("PO", lang),
                      translateText("MO", lang),
                      translateText("Cantidad", lang),
                      translateText("Referencia", lang),
                      translateText("Entrega", lang),
                      translateText("Stamping", lang),
                      translateText("Cant. Term.", lang),
                      translateText("Peso", lang),
                      translateText("Añadir Peso", lang),
                    ].map((header, i) => (
                      <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(9)].map((_, j) => (
                        <TableCell key={j} sx={{ py: 0.75, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      ) : (
        renderList()
      )}

      <AppDialog
        open={confirmVisible}
        onClose={() => {
          setConfirmVisible(false);
          confirmResolverRef.current?.(false);
        }}
        title='Confirmación'
        maxWidth='xs'
        actions={
          <>
            <AppButton
              onClick={() => {
                setConfirmVisible(false);
                confirmResolverRef.current?.(false);
              }}
              variantType='secondary'>
              Cancelar
            </AppButton>
            <AppButton
              onClick={() => {
                setConfirmVisible(false);
                confirmResolverRef.current?.(true);
              }}>
              Aceptar
            </AppButton>
          </>
        }>
        <Typography>{confirmMessage}</Typography>
      </AppDialog>
    </Box>
  );
}
